import axios from "axios";
import { SERVER_URL } from "../ServerLink";

const REFRESH_PATH = "auth/refresh";
const LOGOUT_PATH = "auth/logout";

const api = axios.create({
    baseURL: SERVER_URL + "/",
});

const REFRESH_KEY = "refreshToken";
const ACCESS_KEY = "accessToken";
const ACTIVITY_KEY = "lastActivityAt";

function getAccessToken() { return window.localStorage.getItem(ACCESS_KEY) || ""; }
function getRefreshToken() { return window.localStorage.getItem(REFRESH_KEY) || ""; }
function setTokens({ accessToken, refreshToken }) {
    if (accessToken) window.localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
}
function clearTokens() {
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
    window.localStorage.removeItem("authUser");
    window.localStorage.removeItem("role");
    window.localStorage.removeItem(ACTIVITY_KEY);
}
function markActivity() {
    window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

let refreshInFlight = null;

async function performRefresh() {
    if (refreshInFlight) return refreshInFlight;
    const rt = getRefreshToken();
    if (!rt) {
        return Promise.reject(new Error("NO_REFRESH_TOKEN"));
    }
    refreshInFlight = axios
        .post(SERVER_URL + "/" + REFRESH_PATH, { refreshToken: rt })
        .then((res) => {
            const data = res && res.data;
            if (!data || !data.accessToken || !data.refreshToken) {
                throw new Error("BAD_REFRESH_RESPONSE");
            }
            setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
            return data.accessToken;
        })
        .finally(() => {
            refreshInFlight = null;
        });
    return refreshInFlight;
}

function forceLogoutRedirect() {
    clearTokens();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login?reason=session_expired";
    }
}

api.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = "Bearer " + token;
    }
    markActivity();
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error && error.config;
        const status = error && error.response && error.response.status;
        const url = (original && original.url) || "";

        if (status !== 401 || !original || original.__isRetry) return Promise.reject(error);
        if (url.includes(REFRESH_PATH) || url.includes("/login") || url.includes("/auth/logout")) {
            forceLogoutRedirect();
            return Promise.reject(error);
        }

        try {
            const newAccess = await performRefresh();
            original.__isRetry = true;
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + newAccess;
            return api.request(original);
        } catch (refreshErr) {
            forceLogoutRedirect();
            return Promise.reject(refreshErr);
        }
    }
);

const IDLE_LIMIT_MS = parseInt(window.__RDC_IDLE_LIMIT_MS__ || "0", 10) || 30 * 60 * 1000;
let idleTimer = null;

function resetIdleTimer() {
    markActivity();
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(async () => {
        try {
            const rt = getRefreshToken();
            if (rt) {
                await axios.post(SERVER_URL + "/" + LOGOUT_PATH, { refreshToken: rt }).catch(() => {});
            }
        } finally {
            forceLogoutRedirect();
        }
    }, IDLE_LIMIT_MS);
}

if (typeof window !== "undefined") {
    const events = ["click", "keydown", "scroll", "mousemove", "touchstart", "visibilitychange"];
    events.forEach((ev) => window.addEventListener(ev, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    window.addEventListener("storage", (e) => {
        if (e.key === ACCESS_KEY && !e.newValue) {
            forceLogoutRedirect();
        }
    });
}

export const auth = {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    markActivity,
    forceLogoutRedirect,
    performRefresh,
};

export default api;
